import { useState } from "react";
import { Copy, KeyRound, Wand2 } from "lucide-react";
import { api, errorMessage, fieldErrors } from "@/lib/api";
import { generatePassword, policyError } from "@/lib/passwordPolicy";
import { PasswordInput } from "@/components/form/PasswordInput";
import { PasswordStrength } from "@/components/form/PasswordStrength";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export const TempPasswordDialog = ({ endpoint, email, onDone, testPrefix = "temp" }) => {
    const [open, setOpen] = useState(false);
    const [pwd, setPwd] = useState("");
    const [err, setErr] = useState("");
    const [copied, setCopied] = useState("");
    const [busy, setBusy] = useState(false);
    const id = `${testPrefix}-password`;
    const reset = (o) => { setOpen(o); if (!o) { setPwd(""); setErr(""); setCopied(""); } };
    const copy = async () => {
        try { await navigator.clipboard.writeText(pwd); setCopied("Password copiata negli appunti"); }
        catch (e) { setCopied("Copia non riuscita: seleziona e copia la password manualmente"); }
    };
    const submit = async (e) => {
        e.preventDefault();
        const local = policyError(pwd, email);
        if (!pwd || local) { setErr(pwd ? local : "Campo obbligatorio"); document.getElementById(id)?.focus(); return; }
        setBusy(true);
        try { const { data } = await api.post(endpoint, { password: pwd }); reset(false); onDone({ kind: "success", text: `${data.message}: al primo accesso l'utente dovrà sceglierne una nuova` }); }
        catch (x) { setErr(fieldErrors(x)?.password || errorMessage(x)); }
        setBusy(false);
    };
    return (
        <Dialog open={open} onOpenChange={reset}>
            <DialogTrigger asChild>
                <button type="button" className="btn-secondary py-2" data-testid={`${testPrefix}-open`}><KeyRound aria-hidden="true" size={18} /> Imposta password temporanea</button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid={`${testPrefix}-dialog`}>
                <DialogHeader>
                    <DialogTitle>Imposta password temporanea</DialogTitle>
                    <DialogDescription>Per {email}. Al primo accesso dovrà scegliere una nuova password; le sessioni aperte vengono chiuse.</DialogDescription>
                </DialogHeader>
                <form noValidate onSubmit={submit} className="space-y-4">
                    <div>
                        <label htmlFor={id} className="edi-label">Password temporanea</label>
                        <PasswordInput id={id} autoComplete="new-password" value={pwd} onChange={(e) => { setPwd(e.target.value); setErr(""); setCopied(""); }}
                            aria-invalid={err ? "true" : "false"} aria-describedby={`${id}-strength${err ? ` ${id}-error` : ""}`} data-testid={`${id}-input`} />
                        {err && <p id={`${id}-error`} className="edi-error" role="alert" data-testid={`${id}-error`}>{err}</p>}
                        <PasswordStrength id={`${id}-strength`} password={pwd} email={email} />
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <button type="button" className="btn-secondary py-2" onClick={() => { setPwd(generatePassword()); setErr(""); setCopied(""); }} data-testid={`${testPrefix}-generate`}><Wand2 aria-hidden="true" size={18} /> Genera password sicura</button>
                        <button type="button" className="btn-secondary py-2" disabled={!pwd} onClick={copy} data-testid={`${testPrefix}-copy`}><Copy aria-hidden="true" size={18} /> Copia</button>
                    </div>
                    <p role="status" aria-live="polite" className="text-sm font-semibold min-h-[1.25rem]" data-testid={`${testPrefix}-copy-status`}>{copied}</p>
                    <p className="edi-help">Comunica la password all'utente in modo sicuro, non per email.</p>
                    <div className="flex justify-end gap-3">
                        <button type="button" className="btn-tertiary py-2" onClick={() => reset(false)} data-testid={`${testPrefix}-cancel`}>Annulla</button>
                        <button type="submit" className="btn-primary py-2" disabled={busy} data-testid={`${testPrefix}-save`}>Imposta</button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};
